import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Button,
  Typography,
  Box,
  Chip,
  Link,
  IconButton,
  InputBase,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Badge,
  styled,
  Paper,
  Tooltip,
  ListItemIcon,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
} from '@mui/material';
import {
  Add as AddIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  AccountCircle as AccountIcon,
  NavigateNext as NavigateNextIcon,
  Refresh as RenewIcon,
  PowerSettingsNew as PowerSettingsNewIcon,
  PeopleAlt as PeopleIcon,
  LocalFireDepartment as FireIcon,
  Bolt as BoltIcon,
} from '@mui/icons-material';
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAccount } from '../../contexts/AccountContext';
import { usePlanUpgrade } from '../../contexts/PlanUpgradeContext';
import QueryForm from '../common/QueryForm';
import UpgradeToProButton from '../common/UpgradeToProButton';
import UpgradeToProModal from '../common/UpgradeToProModal';

const Header = ({ user, logout, company_name, subscriptionData }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const currentPlan = subscriptionData?.plan ?? null;
  const voiceCreditsFromAccount = subscriptionData?.credits?.available ?? 0;
  const { openPlansModal } = usePlanUpgrade();
  const { activeAgents, fetchActiveAgents } = useAccount();
  const [showRenewModal, setShowRenewModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showQueryForm, setShowQueryForm] = useState(false);
  const [queryType, setQueryType] = useState('');
  const [agentPopoverAnchorEl, setAgentPopoverAnchorEl] = useState(null);

  const isNumericId = (s) => /^\d+$/.test(String(s));

  // If last segment is numeric (e.g. ID), don't show it — use previous segment for title/breadcrumb
  const getBreadcrumbs = () => {
    const pathnames = location.pathname.split('/').filter((x) => x);
    const showPathnames = pathnames.length > 0 && isNumericId(pathnames[pathnames.length - 1])
      ? pathnames.slice(0, -1)
      : pathnames;

    return showPathnames.map((value, index) => {
      const to = `/${pathnames.slice(0, index + 1).join('/')}`;
      const isLast = index === showPathnames.length - 1;
      const name = value.charAt(0).toUpperCase() + value.slice(1).replace(/-/g, ' ');
      return { name, path: to, isLast };
    });
  };
  
  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleProfileMenuClose();   // close menu first (sync, no issue)
    await logout();             // wait for API call + state/localStorage clear
    navigate('/login');         // only then redirect
  };

  const openQueryForm = (type) => {
    setQueryType(type);
    setShowQueryForm(true);
  };

  const handleAgentPopoverOpen = (event) => {
    setAgentPopoverAnchorEl(event.currentTarget);
    // Refresh agent data when popover opens
    fetchActiveAgents();
  };

  const handleAgentPopoverClose = () => {
    setAgentPopoverAnchorEl(null);
  };

  const agentPopoverOpen = Boolean(agentPopoverAnchorEl);
  const menuOpen = Boolean(anchorEl);
  const breadcrumbs = getBreadcrumbs();

  const getHeaderLink = () => {
    if (location.pathname.startsWith('/integrations')) {
      return { title: 'Integration', path: '/integrations' };
    }
    if (breadcrumbs.length > 0) {
      const last = breadcrumbs[breadcrumbs.length - 1];
      return { title: last.name, path: last.path };
    }
    return { title: 'Dashboard', path: '/dashboard' };
  };

  const headerLink = getHeaderLink();

  const planType = currentPlan?.plan?.plan_type ?? currentPlan?.plan_type ?? 'free';
  const isProPlan = planType === 'pro';
  const creditsValue = currentPlan?.credits?.available ?? currentPlan?.voice_credits ?? voiceCreditsFromAccount;
  const showVoiceCreditsChip = isProPlan || (creditsValue !== undefined && creditsValue !== null);

  // Get user initials for avatar
  const getUserInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Format expiry date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Box>
      <AppBar
        position="static"
        color="transparent"
        elevation={0}
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, md: 64 }, px: { xs: 1, sm: 2 } }}>
          {/* Single breadcrumb item (hyperlinked) */}
          {breadcrumbs.length > 0 || location.pathname.startsWith('/integrations') ? (
            <Link
              component={RouterLink}
              to={headerLink.path}
              underline="hover"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                fontSize: '1.25rem',
                '&:hover': { color: 'primary.main' },
              }}
            >
              {headerLink.title}
            </Link>
          ) : (
            <Link
              component={RouterLink}
              to="/dashboard"
              underline="hover"
              sx={{ fontWeight: 700, color: 'text.primary', fontSize: '1.25rem', '&:hover': { color: 'primary.main' } }}
            >
              Dashboard
            </Link>
          )}

          <Box sx={{ flexGrow: 1 }} />

          {/* Agents Online chip - light green */}
          <Chip
            icon={<PeopleIcon sx={{ fontSize: 18 }} />}
            label={`${activeAgents.count || 0} Agents Online`}
            size="small"
            onClick={handleAgentPopoverOpen}
            sx={{
              fontWeight: 600,
              fontSize: '0.8125rem',
              backgroundColor: 'rgba(46, 125, 50, 0.12)',
              color: '#2e7d32',
              border: 'none',
              mr: 1.5,
              cursor: 'pointer',
              '&:hover': { backgroundColor: 'rgba(46, 125, 50, 0.18)' },
              '& .MuiChip-icon': { color: '#2e7d32' },
            }}
          />
          
          {/* Agent Popover */}
          <Popover
            id="agent-popover"
            open={agentPopoverOpen}
            anchorEl={agentPopoverAnchorEl}
            onClose={handleAgentPopoverClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            PaperProps={{
              elevation: 3,
              sx: {
                width: 300,
                maxHeight: 400,
                overflow: 'auto',
                mt: 1,
                p: 1,
              }
            }}
          >
            <Typography variant="subtitle1" sx={{ p: 1, fontWeight: 600 }}>
              Active Agents
            </Typography>
            <Divider />
            {activeAgents.agents && activeAgents.agents.length > 0 ? (
              <List dense>
                {activeAgents.agents.map((agent) => (
                  <ListItem key={agent.id}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32, fontSize: '13px' }}>
                        {getUserInitials(agent.name)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText 
                      primary={agent.name}
                      secondary={agent.team_name || 'No Team'}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No agents currently online
                </Typography>
              </Box>
            )}
          </Popover>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            {/* Pro Plan chip - light blue */}
            {currentPlan?.plan_name && (
              <Chip
                icon={<BoltIcon sx={{ fontSize: 18 }} />}
                label={currentPlan.plan?.name ?? currentPlan.plan_name}
                size="small"
                sx={{
                  fontWeight: 600,
                  fontSize: '0.8125rem',
                  backgroundColor: 'rgba(25, 118, 210, 0.12)',
                  color: '#1976d2',
                  border: 'none',
                  '& .MuiChip-icon': { color: '#1976d2' },
                }}
              />
            )}

            {/* Expiry — chip-style, red when expired */}
            {(currentPlan?.plan?.end_date ?? currentPlan?.end_date) && (
              <Chip
                size="small"
                label={
                  typeof (currentPlan?.plan?.days_remaining ?? currentPlan?.days_remaining) === 'number' && (currentPlan?.plan?.days_remaining ?? currentPlan?.days_remaining) <= 0
                    ? `Expired ${formatDate(currentPlan.plan?.end_date ?? currentPlan.end_date)}`
                    : `Expires ${formatDate(currentPlan.plan?.end_date ?? currentPlan.end_date)}`
                }
                sx={{
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  height: 28,
                  backgroundColor: typeof (currentPlan?.plan?.days_remaining ?? currentPlan?.days_remaining) === 'number' && (currentPlan?.plan?.days_remaining ?? currentPlan?.days_remaining) <= 0
                    ? 'error.light'
                    : 'grey.100',
                  color: typeof (currentPlan?.plan?.days_remaining ?? currentPlan?.days_remaining) === 'number' && (currentPlan?.plan?.days_remaining ?? currentPlan?.days_remaining) <= 0
                    ? 'error.dark'
                    : 'text.secondary',
                  border: '1px solid',
                  borderColor: typeof (currentPlan?.plan?.days_remaining ?? currentPlan?.days_remaining) === 'number' && (currentPlan?.plan?.days_remaining ?? currentPlan?.days_remaining) <= 0
                    ? 'error.main'
                    : 'grey.300',
                }}
              />
            )}

            {/* Upgrade / Renew for admin */}
            {user?.role === 'AD' && (currentPlan?.plan_type === 'free' || (currentPlan?.plan?.plan_type ?? currentPlan?.plan_type) !== 'pro') && (
              <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                <UpgradeToProButton onClick={() => setShowUpgradeModal(true)} />
                <UpgradeToProModal open={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} />
              </Box>
            )}

            {/* Voice Credits chip - show globally when plan is pro or when credits value is available */}
            {showVoiceCreditsChip && (
              <Tooltip title={
                <>
                  Voice credits (AI analysis of Calls)
                  <br />
                  1 Voice Credit = 1 Minute Call analysis
                </>
              }>
                <Chip
                  icon={<FireIcon sx={{ fontSize: 20, color: '#ea580c' }} />}
                  label={
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                      <span>Voice Credit</span>
                      <span sx={{ color: '#1976d2', fontWeight: 700, fontSize: '0.8125rem' }}>
                        {Number(creditsValue ?? 0).toLocaleString()}
                      </span>
                    </Box>
                  }
                  size="small"
                  variant="outlined"
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.8125rem',
                    height: 28,
                    backgroundColor: '#e6f9f6',
                    borderColor: 'rgba(17, 94, 89, 0.25)',
                    color: '#115e59',
                    '& .MuiChip-icon': { color: '#ea580c' },
                    '& .MuiChip-label': { px: 0.5 },
                    '&:hover': { backgroundColor: '#ccf3ee', borderColor: 'rgba(17, 94, 89, 0.4)' },
                  }}
                />
              </Tooltip>
            )}

            {/* Notifications */}
            <Tooltip title="Notifications">
              <IconButton color="inherit" size="medium" sx={{ color: 'text.primary' }}>
                <Badge badgeContent={0} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            </Tooltip>

            {/* Profile avatar - dark teal */}
            <Tooltip title="Account">
              <IconButton
                onClick={handleProfileMenuOpen}
                size="medium"
                aria-controls={menuOpen ? 'account-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={menuOpen ? 'true' : undefined}
                sx={{ p: 0 }}
              >
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: '#0d9488',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                  }}
                >
                  {getUserInitials(user?.name)}
                </Avatar>
              </IconButton>
            </Tooltip>

            {/* Profile dropdown - redesigned */}
            <Menu
              anchorEl={anchorEl}
              id="account-menu"
              open={menuOpen}
              onClose={handleProfileMenuClose}
              onClick={handleProfileMenuClose}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              PaperProps={{
                elevation: 8,
                sx: {
                  mt: 1.5,
                  width: 240,
                  borderRadius: 2,
                  overflow: 'hidden',
                  boxShadow: '0px 4px 20px rgba(0,0,0,0.08)',
                },
              }}
              MenuListProps={{ disablePadding: true }}
            >
              <Box sx={{ px: 2, py: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '1rem', color: 'text.primary' }}>
                  {user?.name || 'User'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, fontSize: '0.8125rem' }}>
                  {user?.email || ''}
                </Typography>
              </Box>
              <Divider />
              <MenuItem component={RouterLink} to="/profile" sx={{ py: 1.5 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <PersonIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="My Profile" primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </MenuItem>
              {user?.role !== 'AG' && (
                <MenuItem component={RouterLink} to="/settings" sx={{ py: 1.5 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <SettingsIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Settings" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </MenuItem>
              )}
              <MenuItem onClick={handleLogout} sx={{ py: 1.5 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <LogoutIcon fontSize="small" sx={{ color: '#d32f2f' }} />
                </ListItemIcon>
                <ListItemText primary="Logout" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500, color: '#d32f2f' }} />
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      {/* Query Form */}
      <QueryForm
        open={showQueryForm}
        onClose={() => setShowQueryForm(false)}
        queryType={queryType}
      />
    </Box>
  );
};

export default Header;